<!--
  The full record behind a pre-pour row ("MORE"), in the four cards of the stakeholder's prototype:
  Medication, Prescription, Pharmacy, Prescriber. Read-only for now — editing goes through the
  medication's own screens until the backend is wired here.
  Fields come from keyon's GetPatientMeds via utils/prepourMedRecord.js; an order with no pharmacy
  joined says so rather than showing a card of blanks.
-->
<template>
  <div class="rec">
    <section v-for="card in cards" :key="card.title" class="rec-card">
      <h4>{{ card.title }}</h4>
      <p v-if="card.empty" class="rec-empty">{{ card.empty }}</p>
      <dl v-else>
        <template v-for="row in card.rows" :key="row[0]">
          <dt>{{ row[0] }}</dt>
          <dd :class="{ na: !row[1] && row[1] !== 0 }">{{ row[1] || row[1] === 0 ? row[1] : 'Not recorded' }}</dd>
        </template>
      </dl>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({ med: { type: Object, required: true } });

function fmtDate(v){
  if (!v || /^0000/.test(v)) return '';
  const d = new Date(String(v).length === 10 ? v + 'T00:00:00' : v);
  return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const cards = computed(() => {
  const m = props.med, r = m.record || {};
  const period = [fmtDate(r.startDate), fmtDate(r.endDate)].filter(Boolean).join(' – ');
  return [
    { title: 'Medication', rows: [
      ['NDC number', r.ndc || m.ndc], ['RxNorm', r.rxNorm], ['Dose', `${m.dose} × ${m.form}`],
      ['Route', r.route || m.route], ['Tablets / quantity', r.quantity] ] },
    { title: 'Prescription', rows: [
      ['Ordered', fmtDate(r.orderDate)], ['Start – stop', period], ['Refills', r.refills],
      ['Diagnosis', r.diagnosis], ['Instructions', r.instructions] ] },
    r.pharmacy
      ? { title: 'Pharmacy', rows: [['Name', r.pharmacy.name], ['Address', r.pharmacy.address], ['Phone', r.pharmacy.phone], ['NPI', r.pharmacy.npi]] }
      : { title: 'Pharmacy', empty: 'No pharmacy on this medication’s order.' },
    { title: 'Prescriber', rows: [
      ['Prescriber', r.prescriber], ['NPI', r.prescriberNpi], ['Office', r.prescriberPhone], ['Ordered by', r.orderedBy] ] },
  ];
});
</script>

<style scoped>
.rec{ display:grid; grid-template-columns:repeat(auto-fit, minmax(210px, 1fr)); gap:10px; margin-top:10px; }
.rec-card{ background:#fff; border:1px solid #dbe4e4; border-radius:10px; padding:10px 12px; }
.rec-card h4{ margin:0 0 8px; font-size:14px; font-weight:700; color:#245457; text-transform:uppercase; letter-spacing:.04em; }
dl{ display:grid; grid-template-columns:auto 1fr; gap:4px 10px; margin:0; font-size:14px; }
dt{ color:#5f6f6f; }
dd{ margin:0; color:#1c2b2b; word-break:break-word; }
dd.na, .rec-empty{ color:#8a9a9a; font-style:italic; }
.rec-empty{ margin:0; font-size:14px; }
</style>
